import React, { useEffect, useRef } from 'react';

import Card from 'components/Shared/Card/Card';
import SegmentIO from 'reporting/SegmentIO';
import { insightSelectors } from 'store/insight/selectors';
import { fontSize, colors } from 'styles/cp.js';
import { Insight } from 'types/Insight';

export interface Props {
  marginBottom?: number;
  marginTop?: number;
  shouldShowFaasWidget: boolean;
  insight: Pick<Insight, 'financingOffer'>;
}

const FaasWidget: React.FC<Props> = ({ insight, shouldShowFaasWidget = false }) => {
  const hasTrackedRef = useRef(false);

  // Helper method to get uiObjectDetail with fallback logic
  const getUiObjectDetail = (insight: Insight, recipientType: string | undefined): string => {
    // First attempt to use the tracking object detail from EPG
    const trackingObjectDetail =
      insightSelectors.financingOfferTrackingObjectDetailsSelector(insight);
    if (trackingObjectDetail) {
      return trackingObjectDetail;
    }

    // Fallback to the existing logic based on recipientType
    return recipientType === 'NON_QBO_BUSINESS' || recipientType === 'QBO_BUSINESS'
      ? 'faas_widget_B2B'
      : 'faas_widget_B2C';
  };

  const title = insightSelectors.financingOfferTitleSelector(insight as Insight);
  const recipientType = insightSelectors.financingOfferRecipientTypeSelector(insight as Insight);
  const uiObjectDetail = getUiObjectDetail(insight as Insight, recipientType);

  if (!shouldShowFaasWidget) {
    return null;
  }

  const financingOffer = insightSelectors.financingOfferSelector(insight as Insight);
  const { primaryText, secondaryText, continueButtonText, continueButtonUrl, logoImageUrl } =
    financingOffer || {};
  if (!title || !primaryText || !continueButtonText || !continueButtonUrl || !logoImageUrl) {
    return null;
  }

  useEffect(() => {
    if (shouldShowFaasWidget && !hasTrackedRef.current) {
      SegmentIO.transactionViewed({
        ui_object: 'viewed',
        ui_action: 'page',
        ui_access_point: 'transaction_flow',
        ui_object_detail: uiObjectDetail,
      });
      hasTrackedRef.current = true;
    }
  }, [shouldShowFaasWidget, uiObjectDetail]);

  const handleClick = () => {
    SegmentIO.transactionEngaged({
      ui_object: 'link',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      ui_object_detail: uiObjectDetail,
    });
    window.open(continueButtonUrl);
  };
  return (
    <>
      <div className="epg-card-w">
        <Card padding="16px 20px 20px">
          <div className="header">{title}</div>
          <Card bgColor={colors.gray07} padding="16px 20px 20px">
            <div className="logo">
              <img src={`${logoImageUrl}?channel=cp}`} />
            </div>

            <div className="seperator bold">{primaryText}</div>
            {secondaryText}
            <a href="#" onClick={handleClick}>
              {continueButtonText}
            </a>
          </Card>
        </Card>
      </div>
      <style jsx>{`
        .epg-card-w {
          margin-bottom: 20px;
        }

        .header {
          font-size: ${fontSize.sm};
          color: ${colors.black};
          font-family: AvenirNextforINTUIT-regular;
          font-weight: 600;
          margin-bottom: 20px;
        }

        .logo {
          img {
            max-height: 20px;
            max-width: 80px;
          }
        }

        .bold {
          font-family: AvenirNextforINTUIT-Bold;
        }

        .seperator {
          margin: 10px 0;
        }

        a {
          display: flex;
          justify-content: center;
          margin-top: 14px;
          font-size: ${fontSize.xs};
          color: ${colors.green05};
          text-decoration: none;
          border: 2px solid ${colors.green05};
          border-radius: 4px;
          font-family: AvenirNextforINTUIT-Demi;
        }
      `}</style>
    </>
  );
};

export default FaasWidget;
